import { AfterContentInit, Component, EventEmitter, Input, OnInit, Output, SimpleChange } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LicenseService } from 'src/app/core/services/license.service';
import { EntityService } from 'src/app/core/services/entity.service';
import { Policy } from 'src/app/core/models/policy.model';
import { PackageType } from 'src/app/core/models/package-type.model';
import { Entity } from 'src/app/core/models/entity.model';
import { User } from 'src/app/core/models/user.model';
import * as utils from 'src/app/shared/helpers/utils';
import { ConfigService } from 'src/app/core/services/config.service';
import { UserService } from 'src/app/core/services/user.service';
import { Team } from 'src/app/core/models/team.model';
import { catchError, combineLatest, map, Observable, of, tap } from 'rxjs';

@Component({
  selector: 'hbo-policy-actions',
  templateUrl: './policy-actions.component.html',
  styleUrls: ['./policy-actions.component.scss']
})
export class PolicyActionsComponent implements OnInit, AfterContentInit {

  @Input() policy!: Policy;

  @Output() reload: EventEmitter<any> = new EventEmitter();
  @Output() callback: EventEmitter<any> = new EventEmitter();

  action = 'create';

  user: User = utils?.localStorageGetItem('user');
  confirmationModal: any;

  policyForm = this.formBuilder.nonNullable.group({
    id: [''],
    entityId: ['', Validators.required],
    seeLicenses: [false],
    teamId: [''],
    allTeams: [false]
  });

  //entities: Entity[] = [];
  packageTypes: PackageType[] = [];

  teams: Team[] = [];

  //ids of the teams that already have a policy for the selected entity
  takenTeamIds: number[] = [];
  //whether the distributor level policy (team_id null) already exists for the selected entity
  distributorPolicyTaken: boolean = false;
  //package type ids allowed by the distributor-level policy (used to restrict team policy creation)
  distributorPolicyPackageTypeIds: number[] = [];

  packagePolicies: { packageTypeId: number, defaultTestDays: number }[] = [];

  @Input() entity!: Entity;
  reactiveToRole: number = 2;

  childEntities: Entity[] = [];

  clearEntitiesSelector: boolean = false;

  entityId!: number | undefined;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private licenseService: LicenseService,
    private entityService: EntityService,
    private userService: UserService,
    private location: Location,
    public configSvc: ConfigService,

  ) {
    this.childEntities = this.configSvc.currentConfigValue.childEntities!;
  }

  ngOnInit() {
    if (!this.policy) {
      this.policy = { id: 0 } as Policy;
    } else {
      this.action = 'edit';
      this.policyForm.get('entityId')?.disable();
    }
    if (this.user.profileId !== 3) {
      this.policyForm.get('seeLicenses')?.disable();
      this.policyForm.get('teamId')?.disable();
      this.policyForm.get('allTeams')?.disable();
    }
    !this.configSvc.currentConfigValue.packageTypes
      ? this.licenseService.getPackageTypes().subscribe(packageTypes => {
        this.packageTypes = packageTypes.data;
        this.configSvc.setConfigParamsValue('packageTypes', this.packageTypes);
      })
      : this.packageTypes = this.configSvc.currentConfigValue.packageTypes;

    this.policyForm.controls.teamId.valueChanges.subscribe(teamId => {
      if (teamId && this.distributorPolicyPackageTypeIds.length > 0) {
        this.packagePolicies = this.packagePolicies.filter(pp =>
          this.distributorPolicyPackageTypeIds.includes(pp.packageTypeId)
        );
      }
    });
  }

  ngAfterContentInit() {
    if (this.policy && this.policy.id !== 0) {
      this.policyForm.patchValue({
        id: this.policy.id?.toString(),
        entityId: this.policy.entityId?.toString(),
        seeLicenses: this.policy.seeLicenses,
        teamId: this.policy.teamId ? this.policy.teamId.toString() : '',
      });
      this.packagePolicies.push(...this.policy.packagePolicies.map(packagePolicy => ({ packageTypeId: packagePolicy.packageTypeId, defaultTestDays: packagePolicy.defaultTestDays })));
      this.loadEntityPolicyOptions(this.policy.entityId).subscribe();
    }
  }

  save() {
    this.policyForm.markAllAsTouched();
    if (this.policyForm.valid) {
      if (this.action === 'create') {
        const formValue: any = { ...this.policyForm.value, packagePolicies: this.packagePolicies };
        //allTeams is now an explicit control: it creates one policy per team plus the
        //distributor level one, so the team selection is irrelevant in that case
        if (formValue.allTeams) {
          delete formValue.teamId;
        } else {
          delete formValue.allTeams;
          if (!formValue.teamId) delete formValue.teamId;
        }
        this.licenseService.insertPolicy(formValue as Policy).subscribe(res => {
          this.router.navigate(['licenses/policies']);
        });
      } else if (this.action === 'edit') {
        this.licenseService.updatePolicy({ ...this.policyForm.value, packagePolicies: this.packagePolicies } as unknown as Policy).subscribe(res => {
          this.callback.emit({ action: 'updated', result: res, packagePolicies: this.packagePolicies });
        });
      }
    }
  }

  delete() {
    this.licenseService.destroyPolicy(this.policy.id).subscribe(res => {
      this.reload.emit();
    });
  }

  cancel() {
    if (this.action === 'create') {
      this.location.back();
    } else {
      this.reload.emit();
    }
  }

  togglePackage(packageId: number) {
    if (!this.packagePolicies.find(policy => policy.packageTypeId === packageId)) {
      this.packagePolicies.push({ packageTypeId: packageId, defaultTestDays: 0 });
    } else {
      this.packagePolicies = this.packagePolicies.filter(packagePolicy => packagePolicy.packageTypeId !== packageId);
    }
  }

  packageChecked(packageId: number) {
    return this.packagePolicies.find(packagePolicy => packagePolicy.packageTypeId === packageId);
  }

  getPackageTestDays(packageId: number) {
    return this.packagePolicies.find(packagePolicy => packagePolicy.packageTypeId === packageId)?.defaultTestDays;
  }

  setPackageTestDays(packageId: number, days: any) {
    const packagePolicy = this.packagePolicies.find(packagePolicy => packagePolicy.packageTypeId === packageId);
    packagePolicy!.defaultTestDays = days.value;
  }

  onChange(event: any) {
    if (!event.id || this.entityId == event.id) return;

    this.entityId = event.id;
    this.loadEntityPolicyOptions(event.id).subscribe(() => {
      if (this.allPoliciesTaken) {
        //distributor level and every team already have a policy: nothing left to create here
        this.clearEntitiesSelector = true;
        this.teams = [];
        utils.toogleModal('#confirmationModal-policy', false);
        this.entityId = undefined;
        return;
      }
      this.policyForm.controls.entityId.patchValue(event.id.toString());
      this.policyForm.controls.allTeams.patchValue(false);
      //preselect the first option still available
      const firstFreeTeam: Team | undefined = this.teams.find((team: Team) => !this.isTeamTaken(team.id));
      this.policyForm.controls.teamId.patchValue(this.distributorPolicyTaken && firstFreeTeam ? firstFreeTeam.id.toString() : '');
    });
  }

  //loadEntityPolicyOptions()
  //loads the teams of an entity together with the policies it already has, so the form can
  //disable the combinations already taken. The policy being edited never blocks itself.
  loadEntityPolicyOptions(entityId: number): Observable<[Team[], Policy[]]> {
    return combineLatest([this.teamsForEntity$(entityId), this.existingPolicies$(entityId)]).pipe(
      tap(([teams, policies]) => {
        this.teams = teams;
        this.distributorPolicyTaken = policies.some((policy: Policy) => !policy.teamId);
        this.takenTeamIds = policies.filter((policy: Policy) => policy.teamId).map((policy: Policy) => Number(policy.teamId));
        const distributorPolicy = policies.find((p: Policy) => !p.teamId);
        this.distributorPolicyPackageTypeIds = distributorPolicy
          ? distributorPolicy.packagePolicies.map(pp => pp.packageTypeId)
          : [];
        this.syncAllTeamsControl();
      })
    );
  }

  //teamsForEntity$()
  //teams belonging to a distributor entity, reusing the ConfigService cache when already filled
  private teamsForEntity$(entityId: number): Observable<Team[]> {
    const cachedTeams: Team[] | null | undefined = this.configSvc.currentConfigValue.teams;
    if (cachedTeams) return of(this.filterTeamsByEntity(cachedTeams, entityId));

    return this.userService.getTeams({ perPage: -1 }).pipe(map(res => {
      this.configSvc.setConfigParamsValue('teams', res.data);
      return this.filterTeamsByEntity(res.data, entityId);
    }));
  }

  private filterTeamsByEntity(teams: Team[], entityId: number): Team[] {
    return teams.filter((team: Team) => team.entity?.id === Number(entityId));
  }

  //existingPolicies$()
  //policies already created for an entity, excluding the one being edited
  private existingPolicies$(entityId: number): Observable<Policy[]> {
    return this.licenseService.indexPolicies({ entityId, perPage: -1 }).pipe(
      map(result => result.data.filter((policy: Policy) => policy.id !== this.policy?.id))
    );
  }

  isTeamTaken = (teamId: number): boolean => this.takenTeamIds.includes(Number(teamId));

  // Returns true when a package type cannot be selected because it is not present
  // in the distributor-level policy. Only applies to team-level policies.
  isPackageTypeRestrictedByDistributor(packageTypeId: number): boolean {
    if (this.policyForm.controls.allTeams.value) return false;
    const isTeamSelected = !!this.policyForm.controls.teamId.value;
    if (!isTeamSelected || this.distributorPolicyPackageTypeIds.length === 0) return false;
    return !this.distributorPolicyPackageTypeIds.includes(packageTypeId);
  }

  //allTeams fills the gaps: it creates the distributor level policy and one per team for those
  //that do not have one yet. It is only offered while at least one TEAM is still missing,
  //otherwise it would just duplicate what the distributor option already does
  get canCreateAllTeams(): boolean {
    return this.teams.some((team: Team) => !this.isTeamTaken(team.id));
  }

  //nothing left to create for the selected entity
  get allPoliciesTaken(): boolean {
    return this.distributorPolicyTaken && this.teams.every((team: Team) => this.isTeamTaken(team.id));
  }

  //syncAllTeamsControl()
  //reactive forms drive the disabled state from the control itself, so the template cannot rely
  //on [attr.disabled]: Angular overwrites that attribute whenever the control state syncs
  private syncAllTeamsControl() {
    const allTeamsControl = this.policyForm.get('allTeams');
    if (this.user.profileId !== 3 || !this.canCreateAllTeams) {
      allTeamsControl?.disable();
      return;
    }
    allTeamsControl?.enable();
  }

  onChangeObject(e: any) {
    switch (e.type) {
      case 'policy':
        this.clearEntitiesSelector = false;
        break;
      default:
        break;
    }
  }

  closeModal = () => {
    const globalModalShowPb = document.createElement('div');
    document.body.appendChild(globalModalShowPb),
      globalModalShowPb.setAttribute('data-bs-target', '#confirmationModal-policy')
    globalModalShowPb.setAttribute('data-bs-toggle', 'dismiss');
    globalModalShowPb.click();
    document.body.removeChild(globalModalShowPb);
  }



}
